import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';

import { Field, Fields } from 'redux-form';

import TextField from 'components/fields/TextField';
import CheckboxButtonField from 'components/fields/CheckboxButtonField';
import CheckboxField from 'components/fields/CheckboxField';
import MultiselectAutosuggestField from 'components/fields/MultiselectAutosuggestField';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';

import {
  requestFetchHCPCSCodes,
  requestSearchHCPCSCodes,
} from 'redux/modules/hcpcsCodes';

import { accountLoginFail } from 'redux/modules/account';
import {
  requestFetchPatients,
  requestSearchPatients,
} from 'redux/modules/patients';

const styles = theme => ({
  gridItemSeparator: {
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      display: 'block',
      width: 1,
      height: 44,
      top: '50%',
      left: 0,
      transform: 'translateY(-50%)',
      backgroundColor: theme.palette.primary.A100,
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
  },
  gridItemCategories: {
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%'
    },
  },
  gridItemSmall: {
    maxWidth: '11.5%',
    flexBasis: '11.5%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '50%',
      flexBasis: '50%',
    },
  },
  labelCount: {
    marginLeft: 5,
    color: theme.palette.secondary[450],
  },
  checkboxArchive: {
    marginLeft: 16,
  },
});

const OrdersFilterSearch = ({
  variant, 
  className,
  change,
  actions,
  selectedHCPC,
  selectedCities,
  selectedPatients,
  contextFiltersOptions,
  theme,
  classes,
  width,
}) => {
  const contextHcpcsCodes = contextFiltersOptions
    ? contextFiltersOptions.hcpcsCodes : [];
  const remainingHcpcsCodes = contextHcpcsCodes
    .filter(item => selectedHCPC.indexOf(item.ID) === -1);
  const contextCities = contextFiltersOptions
    ? contextFiltersOptions.cities : [];
  const remainingCities = contextCities.filter(item =>
    selectedCities.indexOf(item.name) === -1);
  const fetchHcpcsCodesSuggestions = contextFiltersOptions && contextFiltersOptions.hcpcsCodes
    ? ({ value, offset }) => (new Promise((resolve) => {
      if (offset === 0) {
        if (value.length > 0) {
          resolve(remainingHcpcsCodes
            .filter(item => item.name.toLowerCase().indexOf(value.toLowerCase()) !== -1));
        } else {
          resolve(remainingHcpcsCodes);
        }
      } else {
        resolve([]);
      }
    }))
    : ({ value, limit, offset }) => (
      value.length > 0
      || (selectedHCPC && selectedHCPC.length > 0)
      ? requestSearchHCPCSCodes({
        filters: [
          ...value.length > 0
            ? [{
              name: 'HCPC S Code',
              operation: 'startswith',
              val: value,
              case_sensitivity: false,
            }] : [],
          ...selectedHCPC && selectedHCPC.length > 0
            ? selectedHCPC.map(item => ({
              name: 'ID',
              operation: 'ne',
              val: item,
            }))
            : [],
        ],
        limit,
        offset,
      }).then(res => res.data,
        (err) => {
          if (err.data.error === 'Need authorisation') {
            actions.accountLoginFail();
          }
          return err;
        })
      : requestFetchHCPCSCodes({
        limit,
        offset,
      }).then(res => res.data,
        (err) => {
          if (err.data.error === 'Need authorisation') {
            actions.accountLoginFail();
          }
          return err;
        })
    );

  return (
    <Grid
      className={className}
      container
      justify="space-between"
      alignItems="center"
      spacing={24}
      wrap={width === 'xs' || width === 'sm' ? 'wrap' : 'nowrap'}
    >
      {variant === 'patient' &&
        <Grid
          className={classes.gridItemCategories}
          item
        >
          <Field
            id="Intake"
            name="Intake"
            label="Intake"
            color={theme.palette.states.intake}
            component={CheckboxButtonField}
          />
          <Field
            id="Delivery"
            name="Delivery"
            label="Delivery"
            color={theme.palette.states.delivery}
            component={CheckboxButtonField}
          />
          <Field
            id="Billing"
            name="Billing"
            label="Billing"
            color={theme.palette.states.billing}
            component={CheckboxButtonField}
          />
          <Field
            id="Collection"
            name="Collection"
            label="Collection"
            color={theme.palette.states.collection}
            component={CheckboxButtonField}
          />
          <Field
            id="Paid"
            name="Paid"
            label="Paid"
            color={theme.palette.states.paid}
            component={CheckboxButtonField}
          />
          <Field
            id="Rejected"
            name="Rejected"
            label="Rejected"
            color={theme.palette.states.rejected}
            component={CheckboxButtonField}
          />
          <Field
            className={classes.checkboxArchive}
            id="Archive"
            name="Archive"
            label="Archived Orders"
            component={CheckboxField}
          />
        </Grid>
      }
      {variant === 'patient' ? (
        <Grid
          item
          xs={12}
          sm={4}
        >
          <Grid
            container
            justify="space-between"
            alignItems="center"
            spacing={16}
            wrap={width === 'xs' || width === 'sm' ? 'wrap' : 'nowrap'}
          >
            <Grid
              item
              xs={12}
              sm={6}
            >      
              <Field
                id="Order ID"
                name="Order ID"
                endAdornmentClearButton
                component={TextField}
                placeholder="Search by Order #"
                normalize={normalize.number}
                fullWidth
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <Field
                id="Claim Code"
                name="Claim Code"
                endAdornmentClearButton
                component={TextField}
                placeholder="Search by Claim #"
                normalize={normalize.number}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid
          item
          xs={12}
          md={3}
        >
          <Fields
            names={[
              'Patient Name',
              'Patients',
            ]}
            autosuggestFieldName="Patient Name"
            fieldArrayName="Patients"
            placeholder="Patient Name or PRN"
            type={width === 'xs' || width === 'sm' ? 'text' : 'search'}
            component={MultiselectAutosuggestField}
            renderSuggestionContent={suggestion => `${suggestion['Last Name']}, ${suggestion['First Name']} (PRN: ${suggestion['Record Number']})`}
            renderInputContent={suggestion => `${suggestion['Last Name']}, ${suggestion['First Name']}`}
            onSuggestionSelected={(suggestion) => {
              change('Selected Patient', {
                value: suggestion['Record Number'],
                label: `${suggestion['Last Name']}, ${suggestion['First Name']}`,
              });
              return '';
            }}
            fetchSuggestions={({ value, limit, offset }) => (
              value.length > 0
                || (selectedPatients && selectedPatients.length > 0)
                ? requestSearchPatients({
                  filters: [
                    ...value.length > 0 && !/^\d+$/.test(value)
                      ? [{
                        name: 'Full Name',
                        operation: 'startswith',
                        val: value,
                        case_sensitivity: false,
                      }] : [],
                    ...value.length > 0 && /^\d+$/.test(value)
                      ? [{
                        name: 'Record Number',
                        operation: 'eq',
                        val: value,
                      }] : [],
                    ...selectedPatients && selectedPatients.length > 0
                      ? selectedPatients.map(item => ({
                        name: 'Record Number',
                        operation: 'ne',
                        val: item,
                      }))
                      : [],
                  ],
                  limit,
                  offset,
                }).then(res => res.data,
                  (err) => {
                    if (err.data.error === 'Need authorisation') {
                      actions.accountLoginFail();
                    }
                    return err;
                  })
                : requestFetchPatients({
                  limit,
                  offset,
                }).then(res => res.data,
                  (err) => {
                    if (err.data.error === 'Need authorisation') {
                      actions.accountLoginFail();
                    }
                    return err;
                  })
            )}
            normalize={normalize.hcpcCode}
            keepOpenAfterSelection
          />
        </Grid>
      )}
      {variant !== 'patient' &&
        <Fragment>
          <Grid
            className={classNames(classes.gridItemSeparator, classes.gridItemSmall)}
            item
            xs={6}
            md={2}
          >
            <Field
              id="Patient Date of Birth"
              name="Patient Date of Birth"
              component={TextField}
              type={width === 'xs' || width === 'sm' ? 'text' : 'search'}
              placeholder="Patient DOB"
              fullWidth
              validate={[validate.date]}
              normalize={normalize.date}
            />
          </Grid>
          <Grid
            className={classNames(classes.gridItemSeparator, classes.gridItemSmall)}
            item
            xs={6}
            md={2}
          >
            <Field
              id="Patient Insurance Code"
              name="Patient Insurance Code"
              component={TextField}
              type={width === 'xs' || width === 'sm' ? 'text' : 'search'}
              placeholder="Insurance ID"
              fullWidth
            />
          </Grid>
          <Grid
            className={classes.gridItemSeparator}
            item
            xs={6}
            md={2}
          >
            <Fields
              names={[
                'HCPC Code Name',
                'HCPC Code',
              ]}
              autosuggestFieldName="HCPC Code Name"
              fieldArrayName="HCPC Code"
              placeholder="HCPC S Code"
              type={width === 'xs' || width === 'sm' ? 'text' : 'search'}
              component={MultiselectAutosuggestField}
              renderSuggestionContent={suggestion => (
                <div>
                  <span>{contextFiltersOptions && contextFiltersOptions.hcpcsCodes
                    ? suggestion.name : suggestion['HCPC S Code']}</span>
                  {(suggestion.count && variant !== 'patient') &&
                    <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
                  }
                </div>
              )}
              renderInputContent={suggestion => contextFiltersOptions && contextFiltersOptions.hcpcsCodes
                ? suggestion.name : suggestion['HCPC S Code']}
              onSuggestionSelected={(suggestion) => {
                change('Selected HCPC Code', {
                  value: suggestion.ID,
                  label: contextFiltersOptions && contextFiltersOptions.hcpcsCodes
                  ? suggestion.name : suggestion['HCPC S Code'],
                });
                return '';
              }}
              fetchSuggestions={fetchHcpcsCodesSuggestions}
              normalize={normalize.hcpcCode}
              keepOpenAfterSelection
            />
          </Grid>
          {variant !== 'patient' &&
            <Grid
              className={classNames(classes.gridItemSeparator, classes.gridItemSmall)}
              item
              xs={6}
              md={2}
            >
              <Fields
                id="City"
                names={[
                  'City Name',
                  'City',
                ]}
                autosuggestFieldName="City Name"
                fieldArrayName="City"
                placeholder="City"
                component={MultiselectAutosuggestField}
                fullWidth
                listWidth="auto"
                type={width === 'xs' || width === 'sm' ? 'text' : 'search'}
                renderSuggestionContent={suggestion => (
                  <div>
                    <span>{suggestion.name}</span>
                    {(suggestion.count && variant !== 'patient') &&
                      <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
                    }
                  </div>
                )}
                renderInputContent={suggestion => suggestion.name}
                onSuggestionSelected={(suggestion) => {
                  change('Selected City', {
                    value: suggestion.name,
                    label: suggestion.name === 'No Patient Address' ? null : suggestion.name,
                  });
                  return '';
                }}
                fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                  if (offset === 0) {
                    if (value.length > 0) {
                      resolve(remainingCities
                        .filter(item => item.name.toLowerCase().indexOf(value.toLowerCase()) !== -1));
                    } else {
                      resolve(remainingCities);
                    }
                  } else {
                    resolve([]);
                  }
                }))}
                keepOpenAfterSelection
              />
            </Grid>
          }
          <Grid
            className={classNames(classes.gridItemSeparator, classes.gridItemSmall)}
            item
            xs={12}
            md={2}
          >
            <Field
              id="Order ID"
              name="Order ID"
              component={TextField}
              type={width === 'xs' || width === 'sm' ? 'text' : 'search'}
              placeholder="Order #"
              fullWidth
              normalize={normalize.number}
            />
          </Grid>
          <Grid
            className={classNames(classes.gridItemSeparator, classes.gridItemSmall)}
            item
            xs={12}
            md={2}
          >
            <Field
              id="Claim Code"
              name="Claim Code"
              component={TextField}
              type={width === 'xs' || width === 'sm' ? 'text' : 'search'}
              placeholder="Claim #"
              fullWidth
              normalize={normalize.number}
            />
          </Grid>
        </Fragment>
      }
    </Grid>
  );
};

OrdersFilterSearch.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    'workflow',
    'schedule',
    'patient',
  ]).isRequired,
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
  contextFiltersOptions: PropTypes.shape({
    hcpcsCodes: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      count: PropTypes.number,
    })).isRequired,
  }),
  classes: PropTypes.shape({
    gridItemSeparator: PropTypes.string.isRequired,
  }).isRequired,
};

OrdersFilterSearch.defaultProps = {
  className: '',
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail }, dispatch),
});

export default connect(null, mapDispatchToProps)(
  withWidth()(withStyles(styles, { withTheme: true })(OrdersFilterSearch)),
);



// WEBPACK FOOTER //
// ./src/components/workflow/OrdersFilter/OrdersFilterSearch.js